/*
 * JWL videos (snippets/jwl-product-videos.liquid). Each <jwl-video-tile> holds one video:
 * - with autoplay on, it plays muted on a loop while at least a quarter of it is on screen and pauses when it is not
 *   (nothing is downloaded before that: the video tag has preload="none");
 * - a tap opens it with sound in a popup over the dimmed page -- at most 92% of the screen width and half its
 *   height, so the page stays in view around it. A tap outside the popup, the close button (a dark circle, visible
 *   on white videos too) or Esc closes it, and the silent loops start again.
 * The server renders the videos with their own controls and the tap button hidden, so they still work without
 * JavaScript; this script swaps the two.
 */
if (!window.customElements.get('jwl-video-tile')) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let dialog = null;
  let openedFrom = null;
  let lockedPage = false;

  const playPreviews = () => {
    document.querySelectorAll('jwl-video-tile').forEach((tile) => tile.preview?.());
  };

  // One popup for the whole page, made on the first tap.
  const getDialog = (closeLabel) => {
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'jwl-video-dialog';
      dialog.innerHTML = '<div class="jwl-video-dialog__panel">'
        + '<button type="button" class="jwl-video-dialog__close">'
        + '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
        + '</button><div class="jwl-video-dialog__frame"></div></div>';

      // A click on the dimmed page around the popup lands on the dialog itself.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.querySelector('.jwl-video-dialog__close').addEventListener('click', () => dialog.close());
      dialog.addEventListener('close', () => {
        const video = dialog.querySelector('video');
        if (video) {
          video.pause();
          video.remove();
        }
        if (lockedPage) document.documentElement.classList.remove('lock');
        openedFrom?.button.focus({ preventScroll: true });
        openedFrom = null;
        playPreviews();
      });
      document.body.appendChild(dialog);
    }
    dialog.querySelector('.jwl-video-dialog__close').setAttribute('aria-label', closeLabel || 'Close');
    return dialog;
  };

  class JwlVideoTile extends HTMLElement {
    constructor() {
      super();
      this.watch = this.watch.bind(this);
    }

    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('.jwl-videos__play');
      if (!this.video || !this.button) return;

      this.visible = false;
      this.video.controls = false;
      this.button.hidden = false;
      this.button.addEventListener('click', this.watch);

      if (this.hasAttribute('autoplay') && !reducedMotion.matches) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.preview();
        }, { threshold: 0.25 });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    // The silent on-page loop: playing while visible (and no popup is open), paused otherwise.
    preview() {
      const video = this.video;
      if (!video) return;
      video.muted = true;
      if (this.observer && this.visible && !dialog?.open) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }

    watch() {
      const popup = getDialog(this.dataset.closeLabel);

      // A copy of this video (same sources and poster) plays in the popup, from the start, with sound.
      const video = this.video.cloneNode(true);
      video.removeAttribute('class');
      video.setAttribute('playsinline', '');
      video.controls = true;
      video.loop = false;
      video.muted = false;
      video.preload = 'auto';
      popup.querySelector('.jwl-video-dialog__frame').replaceChildren(video);

      document.querySelectorAll('jwl-video-tile').forEach((tile) => tile.video?.pause());
      openedFrom = this;
      lockedPage = !document.documentElement.classList.contains('lock');
      if (lockedPage) document.documentElement.classList.add('lock');
      popup.showModal();
      video.play().catch(() => {});
    }
  }

  window.customElements.define('jwl-video-tile', JwlVideoTile);
}
